import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ScrollText, ArrowLeft, AlertCircle } from 'lucide-react';
import { getBackendUrl } from '../services/origin';

const API = getBackendUrl();

/**
 * Public Event Terms & Conditions page.
 *
 * Two modes (one component, one URL pattern each):
 *   /event/termini-e-condizioni     → master brand-level T&C
 *   /event/:slug/termini            → per-event T&C (with override or fallback)
 *
 * The HTML is rendered server-side from Markdown by the public endpoint
 * so the browser doesn't need a Markdown parser. Placeholders are merged
 * with real brand/event data; `version_hash` is the SHA256 of the
 * rendered Markdown (stored on each ticket purchase later for legal
 * proof of which exact text the buyer accepted).
 *
 * Hosted on the customer-facing production domain (e.g.
 * `ring.tacoloco.food/event/termini-e-condizioni`) so the URL is
 * shareable/printable on flyers and ticket confirmations.
 */
export default function EventTermsPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isPerEvent = !!slug;

  useEffect(() => {
    let cancelled = false;
    document.title = isPerEvent
      ? 'Termini e Condizioni — Evento'
      : 'Termini e Condizioni di Vendita';
    const url = isPerEvent
      ? `${API}/api/public/events/${encodeURIComponent(slug)}/terms`
      : `${API}/api/public/event-terms`;
    axios.get(url)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.detail || 'Impossibile caricare i Termini e Condizioni.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, isPerEvent]);

  const branding = data?.branding || {};
  const bg = branding.background_color || '#FAFAFA';
  const tc = '#1A1A1A';        // body text (use dark on light always — readability)
  const ts = '#64748B';
  const pc = branding.primary_color || '#D44E20';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bg }} data-testid="event-terms-loading">
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: `${pc}30`, borderTopColor: pc }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: bg }} data-testid="event-terms-error">
        <div className="max-w-sm text-center">
          <AlertCircle size={32} style={{ color: pc }} className="mx-auto mb-3" />
          <h1 className="text-lg font-bold mb-1" style={{ color: tc }}>Pagina non disponibile</h1>
          <p className="text-sm" style={{ color: ts }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }} data-testid="event-terms-page">
      {/* Header bar */}
      <header
        className="sticky top-0 z-30 border-b"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/')}
            data-testid="event-terms-back-btn"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-slate-100"
            aria-label="Indietro">
            <ArrowLeft size={18} style={{ color: tc }} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}15` }}>
              <ScrollText size={15} style={{ color: pc }} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: ts }}>{data?.business_name || ''}</p>
              <p className="text-sm font-bold truncate" style={{ color: tc }}>
                {isPerEvent && data?.event_name ? `T&C · ${data.event_name}` : 'Termini e Condizioni'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {isPerEvent && data?.event_date && (
          <div className="mb-6 rounded-xl border p-4" style={{ borderColor: `${pc}20`, backgroundColor: `${pc}08` }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: pc }}>Evento</p>
            <p className="text-base font-bold mt-1" style={{ color: tc }}>{data.event_name}</p>
            <p className="text-sm mt-0.5" style={{ color: ts }}>
              {data.event_date}{data.event_time ? ` · ore ${data.event_time}` : ''}
            </p>
            {data.uses_override && (
              <p className="mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: `${pc}15`, color: pc }}>
                Termini specifici per questo evento
              </p>
            )}
          </div>
        )}

        <article
          className="prose prose-slate max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-3 prose-p:leading-relaxed prose-p:text-[15px] prose-li:text-[15px] prose-strong:text-[#1A1A1A]"
          style={{ color: tc }}
          data-testid="event-terms-content"
          dangerouslySetInnerHTML={{ __html: data?.rendered_html || '' }}
        />

        {data?.version_hash && (
          <footer className="mt-12 pt-6 border-t" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-[10px] text-center font-mono" style={{ color: '#94A3B8' }}>
              version: {data.version_hash.slice(0, 16)}
            </p>
            {data?.public_url && (
              <p className="text-[10px] text-center mt-1" style={{ color: '#94A3B8' }}>
                {data.public_url}
              </p>
            )}
          </footer>
        )}
      </main>
    </div>
  );
}
